"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import React from "react";
import { isPublicArtifactLinkPath } from "@/lib/public-artifact-link";
import { isSensitiveAuthPath, SENSITIVE_URL_PARAMS } from "@/lib/sensitive-auth-path";

/**
 * Loads the Google Ads global site tag (gtag.js) and configures cross-domain
 * linking between brev.io (marketing, Framer) and app.brev.io. Without the
 * linker, the GCLID cookie never crosses the domain boundary and every
 * conversion looks "direct" in Google Ads reporting.
 *
 * Gated on NEXT_PUBLIC_GOOGLE_ADS_ID — renders nothing if unset (local/dev).
 */
export function GoogleAdsScript() {
  const adsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  const pathname = usePathname();

  // BRE-8058: gtag's config sends the page URL, and a public link's URL is its
  // bearer token. BRE-9668: so do login, consent and OAuth callback URLs.
  if (!adsId || isPublicArtifactLinkPath(pathname ?? "") || isSensitiveAuthPath(pathname ?? "")) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${adsId}`}
        strategy="afterInteractive"
      />
      {/* copy-length-ok: inline gtag script, not user-visible copy */}
      <Script id="google-ads-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          function brevRedactUrl(u, depth) {
            ${JSON.stringify(SENSITIVE_URL_PARAMS)}.forEach(function (p) { u.searchParams.delete(p); });
            if (depth >= 3) return;
            Array.from(u.searchParams.entries()).forEach(function (entry) {
              var value = entry[1];
              if (!new RegExp('^(/(?!/)|https?://)', 'i').test(value)) return;
              try {
                var nested = new URL(value, u.origin);
                brevRedactUrl(nested, depth + 1);
                u.searchParams.set(entry[0], value.charAt(0) === '/' ? nested.pathname + nested.search + nested.hash : nested.toString());
              } catch (e) {}
            });
          }
          function brevRedact(href) {
            try {
              var u = new URL(href);
              brevRedactUrl(u, 0);
              return u.toString();
            } catch (e) { return ''; }
          }
          gtag('config', '${adsId}', {
            page_location: brevRedact(location.href),
            page_referrer: brevRedact(document.referrer),
            linker: {
              domains: ['brev.io', 'app.brev.io'],
              accept_incoming: true
            }
          });
        `}
      </Script>
    </>
  );
}
